import { Typography } from 'antd';
import ModalNote from 'components/Checkout/ProductNoteModal';
import NewMarketItem from 'components/MarketItem/NewMarketItem';
import { Viewport } from 'components/ViewportProvider';
import { push } from 'connected-react-router';
import { setHomeFavirote, setHomeProductNote } from 'containers/HomePage/actions';
import { selectLoading, selectNoteItemLoading, selectSections } from 'containers/HomePage/selectors';
import {
  IHomeFavoritePayload,
  IHomeProductNotePayload,
  ILoadingSection,
  ISection,
  ISectionData,
} from 'containers/HomePage/types';
import {
  selectAddedProducts,
  selectAddingProducts,
  selectBuyer,
  selectCategories,
  selectUserPermissions,
} from 'containers/MainLayout/selectors';
import { AddedProduct, AddingProduct } from 'containers/MainLayout/types';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styles/styled-components';
import { Mousewheel, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ApplicationRootState } from 'types';
import {
  Buyer,
  Category,
  Product,
  RecommendationItem,
  ShowcaseItem,
  TrackingAddToCartAddFromType,
  TrackingProductImpressionType,
} from 'types/schema';
import { commonDimensions } from 'utils/constants';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';
import { isRecommendationTag } from 'utils/utilities';
import generateProductSlug from '../../utils/generateProductSlug';
import Arrow from './Arrow';
import bestPriceBg from './images/bestPriceBg.png';
import bestPriceBgMobile from './images/bestPriceBgMobile.png';

dayjs.extend(isoWeek);

const start = dayjs().startOf('isoWeek');
const end = dayjs().endOf('isoWeek');

interface ITrackingFrom {
  productImpression: TrackingProductImpressionType;
  addToCart: TrackingAddToCartAddFromType;
}

const { Title } = Typography;

const Container = styled.div`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  margin: 0 auto;
  display: flex;
  align-items: stretch;
  gap: 12px;
  padding: 0 40px;
  @media (max-width: 767px) {
    padding: 0;
  }
`;

const SectionTitle = styled('div')`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 20px;
  margin-bottom: 16px;
  align-items: center;
  @media (max-width: 767px) {
    padding: 0 16px;
    margin-bottom: 12px;
  }
  h2 {
    margin-bottom: 0px;
  }
  ${({ isTopGrade }) =>
    isTopGrade &&
    `
    background: linear-gradient(90deg, #00694D 0%, #00956B 100%);
    padding: 12px 20px !important;
    align-items: center;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    margin-bottom: 0px;
    h2 {
      margin-bottom: 0px;
      color: #ffffff;
    }
    @media (max-width: 767px) {
      border-radius: 0px;
      padding: 16px 16px !important;
      margin-bottom: 0px;
    }
  `}
`;

const SectionHeading = styled(Title)`
  flex-basis: 75%;
  &.ant-typography {
    font-size: 24px;
    line-height: 1.5;
  }
  @media (max-width: 991px) {
    &.ant-typography {
      font-size: 16px;
      line-height: 20px;
    }
  }
`;
const TopGradeTitle = styled.div`
  color: #ffffff;
  font-size: 24px;
  font-weight: 600;
  @media (max-width: 767px) {
    font-size: 16px;
  }
`;
const TopGradeTitleHighlight = styled.span`
  color: #e7d1a8;
`;

const WidgetWrapper = styled('div')`
  display: grid;
  padding: 16px 0 8px;
  background-color: #fff;
  margin-bottom: 20px;
  border-radius: 16px;
  flex-grow: 1;
  overflow: hidden;
  box-shadow: 0 4px 24px 0 rgba(236, 236, 236, 0.8);
  @media (max-width: 576px) {
    border-radius: 0px;
    padding: 16px 0 12px;
    margin-bottom: 12px;
  }
  ${({ isTopGrade }) =>
    isTopGrade &&
    `
      padding: 0px !important;
      background: linear-gradient(107deg, #DEF7F7 19.63%, #E7F9DC 80.37%);
    `}
`;

const List = styled('div')`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  ${({ gap }) =>
    gap &&
    `
    gap: 19px;
  `}
  ${({ isTopGrade }) =>
    isTopGrade &&
    `
      padding: 20px 0px;
      @media (max-width: 576px) {
        padding: 12px 0px 16px;
      }
    `}
`;

const Item = styled('div')`
  width: 100%;
  height: 100%;
`;
const BestDealWrapper = styled.div`
  background: #fef2f2;
  border-radius: 80px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BestDealText = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #111827;
  @media (max-width: 767px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const SwiperSlideElement = styled(SwiperSlide)`
  ${media.md`
  width: ${({ width }) => (width ? width : '235px')} !important;
  `}
  width: ${({ width }) => (width ? width : '152')}px !important;
`;

const SwiperElement = styled(Swiper)`
  max-width: 1392px;
  margin: 0px 0px 0px !important;
  padding: ${({ isBestPrices }) => (isBestPrices ? '1px 20px 1px 0px' : '1px 20px')}!important;
  @media (max-width: 767px) {
    padding: 1px 16px !important;
  }
  .swiper-button-next,
  .swiper-button-prev {
    width: 40px !important;
    height: 40px !important;
    border-radius: 50% !important;
    background-color: #ffffff !important;
    border: 1px solid #ffffff;
    box-shadow: 2px 6px 32px 0 rgba(0, 0, 0, 0.1);
    top: 50% !important;
    transform: translateY(-50%);
    &:after {
      font-size: 14px !important;
      color: #596056 !important;
      font-weight: 700;
    }
    &.swiper-button-disabled {
      display: none !important;
    }
    &:hover {
      border-color: #43a047;
      &:after {
        color: #43a047 !important;
      }
    }
  }
  @media (max-width: 767px) {
    .swiper-button-next,
    .swiper-button-prev {
      display: none;
    }
  }
  .swiper-button-next {
    right: 8px;
    &:after {
      margin-left: 1px;
    }
  }
  .swiper-button-prev {
    left: 8px;
    &:after {
      margin-right: 1px;
    }
  }
  .swiper-wrapper {
    height: fit-content !important;
  }
`;

const BannerWrapper = styled.div`
  position: relative;
  height: fit-content;
  @media (min-width: 768px) {
    margin-left: 20px;
    margin-top: 1px;
  }
`;

const BannerTextbox = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 20px;
  width: 100%;
  text-align: center;
`;

const BannerText = styled.div`
  font-size: 36px;
  font-weight: 700;
  line-height: 52px;
  color: #2f7434;
  display: flex;
  flex-direction: column;
  span {
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    margin-top: -7px;
  }
  @media (max-width: 991px) {
    font-size: 24px;
    line-height: 32px;
    span {
      font-size: 12px;
      font-weight: 600;
      line-height: 18px;
      margin-top: 0px;
    }
  }
`;

const BannerImage = styled.img`
  width: ${({ width }) => (width ? width : '240')}px;
  border-radius: 16px;
`;

const BannerTime = styled.div`
  color: #82b535;
  font-size: ${({ isMobile }) => (isMobile ? '12' : '14')}px;
  font-weight: ${({ isMobile }) => (isMobile ? '600' : '600')};
  width: fit-content;
  text-align: center;
  background: #ffffff;
  border-radius: 80px;
  padding: 2px 8px;
  margin: 12px auto 0;
`;

const SeeAllWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #3c9041;
  &:hover {
    span {
      opacity: 0.8;
    }
    svg {
      opacity: 0.8;
    }
  }
  ${({ isTopGrade }) =>
    isTopGrade &&
    `
    color: #ffffff
    &:hover {
    span {
      opacity: 0.8;
    }
    svg {
      opacity: 0.8;
    }
  }
  `}
  ${({ isBestDeal }) =>
    isBestDeal &&
    `
    color: #E56910
    &:hover {
    span {
      opacity: 0.8;
    }
    svg {
      opacity: 0.8;
    }
  }
  `}
`;

interface IOwnProps {
  showcaseId: string;
  showcase: ShowcaseItem | RecommendationItem;
}

interface IStateProps {
  sections: ISection;
  categories: Category[];
  loading: ILoadingSection | boolean;
  addedProducts: AddedProduct;
  addingProducts: AddingProduct;
  permissions: string[];
  noteItemLoading: boolean;
  buyer: Buyer;
}

interface IDispatchProps {
  onToggleFavorite: (data: IHomeFavoritePayload) => void;
  onSetProductNote: (data: IHomeProductNotePayload) => void;
  gotoSignIn: () => void;
}

type Props = IStateProps & IDispatchProps & IOwnProps;

const KamereoLogo = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="107" height="18" viewBox="0 0 107 18" fill="none">
      <path
        d="M37.8549 8.25077L41.924 14.413H39.3208L36.2953 9.67671L35.1227 10.9316V14.413H32.9629V3.58594H35.1227V8.4392H35.2284L39.3674 3.58594H41.7125L37.8549 8.25077Z"
        fill="#43A047"
      />
      <path
        d="M42.709 14.4135L46.3796 3.58594H48.9123L52.5709 14.4135H50.2258L49.3698 11.628H45.7225L44.8546 14.4135H42.709ZM47.5402 5.65144L46.2034 10.039H48.9708L47.6363 5.65144H47.5402Z"
        fill="#43A047"
      />
      <path
        d="M54.2949 14.4135V3.58594H56.4985L59.6059 10.6172H59.7468L62.8644 3.58594H65.0452V14.4135H63.0315V7.20676H62.9258L60.3459 13.009H58.9249L56.3235 7.20676H56.2297V14.4135H54.2949Z"
        fill="#43A047"
      />
      <path
        d="M74.8055 14.4135H67.7695V3.58594H74.8055V5.33813H69.9038V8.11183H74.5116V9.77121H69.9038V12.6726H74.8055V14.4135Z"
        fill="#43A047"
      />
      <path
        d="M83.6598 3.97027C84.2006 4.22042 84.6537 4.62444 84.9614 5.13071C85.2733 5.68338 85.4303 6.30852 85.4161 6.94141C85.4269 7.42915 85.3267 7.91305 85.1228 8.35722C84.7901 9.09638 84.1804 9.6788 83.4222 9.98173L85.6303 14.3473H83.2051L81.2936 10.3085H79.3446V14.4148H77.2109V3.58777H81.5875C82.299 3.55209 83.009 3.68315 83.6598 3.97027ZM81.2328 8.71723C81.7433 8.74929 82.2483 8.59677 82.6537 8.28804C82.8356 8.13032 82.9779 7.93292 83.0695 7.71141C83.161 7.48991 83.1993 7.25037 83.1812 7.01172C83.1972 6.7646 83.1582 6.51701 83.0671 6.2864C82.976 6.05578 82.8349 5.84773 82.6537 5.6769C82.2646 5.36248 81.7715 5.20213 81.2697 5.2269H79.3446V8.70541L81.2328 8.71723Z"
        fill="#43A047"
      />
      <path
        d="M94.7762 14.4135H87.7402V3.58594H94.7762V5.33813H89.8745V8.11183H94.4829V9.77121H89.8745V12.6726H94.7762V14.4135Z"
        fill="#43A047"
      />
      <path
        d="M105.54 12.3362C105.192 13.0502 104.632 13.6415 103.934 14.0305C103.165 14.4443 102.299 14.6485 101.424 14.6222C100.55 14.6458 99.6848 14.4417 98.9151 14.0305C98.2163 13.6387 97.6525 13.0485 97.2963 12.3362C96.9026 11.514 96.7098 10.6116 96.7336 9.70202V8.30927C96.7084 7.39648 96.9012 6.49068 97.2963 5.66551C97.6525 4.95318 98.2163 4.36298 98.9151 3.97125C99.6846 3.55922 100.55 3.35439 101.424 3.37725C102.299 3.35097 103.165 3.55519 103.934 3.969C104.633 4.3622 105.193 4.95753 105.54 5.67507C105.934 6.49727 106.127 7.39964 106.103 8.30927V9.70202C106.127 10.6117 105.934 11.514 105.54 12.3362ZM103.593 6.53401C103.416 6.11233 103.114 5.75361 102.727 5.50503C102.34 5.25644 101.886 5.12962 101.424 5.14126C100.963 5.12333 100.507 5.24772 100.12 5.49722C99.7335 5.74672 99.4345 6.10901 99.2646 6.53401C99.0267 7.08323 98.9145 7.67776 98.9361 8.27496V9.77234C98.9159 10.3656 99.028 10.956 99.2646 11.5015C99.4345 11.9265 99.7335 12.2888 100.12 12.5383C100.507 12.7878 100.963 12.9121 101.424 12.8942C101.886 12.9059 102.34 12.779 102.727 12.5305C103.114 12.2819 103.416 11.9231 103.593 11.5015C103.82 10.9534 103.928 10.3643 103.91 9.77234V8.35596C103.938 7.74031 103.83 7.12603 103.593 6.55595V6.53401Z"
        fill="#43A047"
      />
      <path
        d="M14.4924 12.375C14.2675 12.375 14.0477 12.441 13.8608 12.5646C13.6738 12.6883 13.528 12.864 13.442 13.0696C13.3559 13.2752 13.3335 13.5014 13.3773 13.7197C13.4212 13.938 13.5295 14.1385 13.6885 14.2958C13.8475 14.4532 14.0501 14.5604 14.2706 14.6038C14.4911 14.6472 14.7197 14.625 14.9275 14.5398C15.1352 14.4546 15.3128 14.3104 15.4377 14.1253C15.5626 13.9403 15.6293 13.7228 15.6293 13.5002C15.6293 13.2018 15.5095 12.9156 15.2963 12.7046C15.0831 12.4936 14.7939 12.375 14.4924 12.375ZM14.4924 13.7815C14.4362 13.7815 14.3812 13.765 14.3344 13.7341C14.2877 13.7032 14.2513 13.6592 14.2298 13.6078C14.2083 13.5564 14.2027 13.4999 14.2136 13.4453C14.2246 13.3908 14.2517 13.3407 14.2914 13.3013C14.3312 13.262 14.3818 13.2352 14.4369 13.2243C14.4921 13.2135 14.5492 13.219 14.6011 13.2403C14.653 13.2616 14.6975 13.2976 14.7287 13.3439C14.76 13.3902 14.7766 13.4446 14.7766 13.5002C14.7766 13.5748 14.7466 13.6464 14.6933 13.6991C14.64 13.7519 14.5678 13.7815 14.4924 13.7815Z"
        fill="#43A047"
      />
      <path
        d="M14.4933 10.6875C13.9312 10.6875 13.3817 10.8525 12.9144 11.1615C12.4471 11.4705 12.0828 11.9098 11.8677 12.4237C11.6526 12.9376 11.5963 13.5031 11.706 14.0487C11.8156 14.5943 12.0863 15.0954 12.4837 15.4887C12.8812 15.8821 13.3876 16.15 13.9388 16.2585C14.4901 16.367 15.0615 16.3113 15.5808 16.0984C16.1001 15.8856 16.5439 15.5251 16.8562 15.0625C17.1685 14.6 17.3352 14.0563 17.3352 13.5C17.3352 12.7541 17.0357 12.0387 16.5028 11.5113C15.9698 10.9838 15.247 10.6875 14.4933 10.6875ZM14.4933 15.4688C14.0998 15.4688 13.7152 15.3533 13.3881 15.137C13.0609 14.9206 12.806 14.6131 12.6554 14.2534C12.5048 13.8937 12.4654 13.4978 12.5422 13.1159C12.6189 12.734 12.8084 12.3832 13.0866 12.1079C13.3648 11.8326 13.7192 11.6451 14.1051 11.5691C14.491 11.4931 14.891 11.5321 15.2545 11.6811C15.618 11.8302 15.9287 12.0825 16.1473 12.4062C16.3659 12.73 16.4826 13.1106 16.4826 13.5C16.4826 14.0222 16.273 14.5229 15.8999 14.8921C15.5268 15.2613 15.0209 15.4688 14.4933 15.4688Z"
        fill="#43A047"
      />
      <path
        d="M14.9276 4.51464C14.9197 4.51423 14.9119 4.51423 14.904 4.51464C14.821 4.51735 14.7405 4.54399 14.6726 4.59134C14.6047 4.63869 14.5523 4.70467 14.5217 4.78113C14.4912 4.85759 14.4839 4.94121 14.5007 5.02173C14.5175 5.10225 14.5577 5.1762 14.6164 5.23439C14.651 5.27124 14.6925 5.30105 14.7387 5.32214C14.7953 5.3463 14.856 5.35946 14.9177 5.36093C16.0824 5.4421 17.1974 5.86003 18.1242 6.56284C19.051 7.26565 19.7488 8.22237 20.131 9.31424C20.5132 10.4061 20.5629 11.585 20.274 12.7046C19.9851 13.8242 19.3702 14.8352 18.5058 15.612C18.8741 14.9274 19.0573 14.1603 19.0376 13.385C19.0179 12.6098 18.7961 11.8528 18.3935 11.1874C17.991 10.5221 17.4214 9.97099 16.7402 9.58773C16.059 9.20447 15.2892 9.00201 14.5056 9.00003H6.2499C6.2499 6.83685 7.11819 4.76227 8.66377 3.23267C10.2094 1.70307 12.3056 0.843753 14.4914 0.843753V2.95314C14.4915 3.01758 14.5065 3.08117 14.5352 3.13901C14.564 3.19685 14.6057 3.24744 14.6571 3.28691C14.7086 3.32639 14.7685 3.35369 14.8323 3.36677C14.896 3.37985 14.962 3.37835 15.0251 3.36238C15.1162 3.34071 15.1973 3.2892 15.2552 3.21626C15.313 3.14331 15.3443 3.05315 15.344 2.96041V0.421876C15.3414 0.312489 15.2959 0.208382 15.2171 0.131699C15.1382 0.0550165 15.0322 0.0118186 14.9216 0.011261C14.9216 0.011261 14.6346 0 14.4925 0C12.0806 0 9.76749 0.948208 8.06202 2.63604C6.35655 4.32387 5.39844 6.61307 5.39844 9.00003C5.39844 9.14066 5.40982 9.42472 5.40982 9.42472C5.41057 9.53612 5.45581 9.64271 5.53568 9.72122C5.61554 9.79973 5.72353 9.84379 5.8361 9.84378H11.8484C11.0647 10.3975 10.479 11.1834 10.1759 12.088C9.87277 12.9926 9.86788 13.9691 10.1619 14.8766C10.456 15.7841 11.0338 16.5756 11.812 17.137C12.5901 17.6984 13.5284 18.0007 14.4914 18.0001C16.2604 17.9969 17.9589 17.3137 19.2285 16.0945C20.4981 14.8754 21.2395 13.2159 21.2961 11.4661C21.3527 9.71625 20.7202 8.01308 19.5319 6.7161C18.3437 5.41912 16.6928 4.62978 14.9276 4.51464ZM10.8254 13.9501C10.7306 13.1942 10.8766 12.428 11.2432 11.7583C11.6098 11.0886 12.1785 10.5489 12.87 10.2146C13.5615 9.88025 14.3412 9.76797 15.1003 9.89343C15.8594 10.0189 16.5599 10.3758 17.104 10.9144C17.6482 11.453 18.0089 12.1462 18.1357 12.8974C18.2625 13.6487 18.149 14.4203 17.8112 15.1047C17.4734 15.789 16.928 16.3519 16.2513 16.7147C15.5747 17.0774 14.8004 17.222 14.0367 17.1282C13.2204 17.0276 12.4614 16.6604 11.8797 16.0847C11.298 15.5091 10.927 14.7579 10.8254 13.9501Z"
        fill="#43A047"
      />
      <path
        d="M11.0814 3.375C10.7441 3.375 10.4143 3.47398 10.1339 3.65941C9.85343 3.84484 9.63489 4.10836 9.50582 4.41671C9.37674 4.72506 9.34293 5.06441 9.40874 5.39175C9.47454 5.7191 9.63697 6.01976 9.87547 6.25576C10.114 6.49176 10.4178 6.65249 10.7486 6.7176C11.0794 6.78271 11.4224 6.74926 11.734 6.62154C12.0456 6.49382 12.3119 6.27756 12.4993 6.00005C12.6867 5.72255 12.7867 5.39626 12.7867 5.06251C12.7867 4.61495 12.6071 4.18572 12.2872 3.86925C11.9674 3.55278 11.5336 3.375 11.0814 3.375ZM11.0814 6.18751C10.8565 6.18751 10.6367 6.12154 10.4498 5.99793C10.2628 5.87431 10.117 5.6986 10.031 5.49303C9.94492 5.28747 9.92243 5.06128 9.9663 4.84305C10.0102 4.62482 10.1185 4.42436 10.2775 4.26702C10.4365 4.10969 10.639 4.00254 10.8596 3.95913C11.0801 3.91572 11.3087 3.93798 11.5164 4.02313C11.7242 4.10828 11.9018 4.25251 12.0267 4.43752C12.1516 4.62252 12.2183 4.84 12.2183 5.06251C12.2183 5.36088 12.0985 5.64701 11.8853 5.85799C11.672 6.06897 11.3829 6.18751 11.0814 6.18751Z"
        fill="#43A047"
      />
      <path
        d="M3.26863 0C3.15556 0 3.04715 0.0444794 2.96719 0.123597C2.88724 0.202714 2.84229 0.309988 2.84229 0.421876V5.62502H2.27383V0.421876C2.27383 0.309988 2.22888 0.202714 2.14893 0.123597C2.06897 0.0444794 1.96056 0 1.84749 0C1.73441 0 1.62601 0.0444794 1.54605 0.123597C1.4661 0.202714 1.42115 0.309988 1.42115 0.421876V5.62502H0.852688V0.421876C0.852688 0.309988 0.807738 0.202714 0.727783 0.123597C0.647829 0.0444794 0.539419 0 0.426346 0C0.313273 0 0.204863 0.0444794 0.124908 0.123597C0.0449534 0.202714 3.11941e-06 0.309988 3.11941e-06 0.421876V7.03127C-0.000378213 7.17053 0.034208 7.30769 0.10069 7.43042C0.167173 7.55316 0.263491 7.65762 0.380894 7.7344C0.521165 7.82533 0.684999 7.87417 0.852688 7.87503L0.56846 16.7344C0.56846 17.0701 0.703242 17.392 0.943106 17.6293C1.18297 17.8667 1.50827 18.0001 1.84749 18.0001C2.18671 18.0001 2.51201 17.8667 2.75187 17.6293C2.99173 17.392 3.12652 17.0701 3.12652 16.7344L2.84229 7.87503C3.00998 7.87417 3.17381 7.82533 3.31408 7.7344C3.43148 7.65762 3.5278 7.55316 3.59429 7.43042C3.66077 7.30769 3.69535 7.17053 3.69497 7.03127V0.421876C3.69497 0.309988 3.65002 0.202714 3.57007 0.123597C3.49011 0.0444794 3.3817 0 3.26863 0ZM1.84749 17.1563C1.73441 17.1563 1.62601 17.1118 1.54605 17.0327C1.4661 16.9536 1.42115 16.8463 1.42115 16.7344L1.45952 15.5346L1.70537 7.87503H1.9896L2.23546 15.5346L2.27383 16.7344C2.27383 16.8463 2.22888 16.9536 2.14893 17.0327C2.06897 17.1118 1.96056 17.1563 1.84749 17.1563ZM2.84229 6.6094C2.84229 6.72129 2.79734 6.82856 2.71738 6.90768C2.63743 6.98679 2.52902 7.03127 2.41595 7.03127H1.27903C1.16596 7.03127 1.05755 6.98679 0.977594 6.90768C0.897639 6.82856 0.852688 6.72129 0.852688 6.6094V6.46877H2.84229V6.6094Z"
        fill="#43A047"
      />
      <path
        d="M25.88 0.00817199C25.8554 0.00271428 25.8302 -3.60594e-05 25.805 8.78851e-07H25.7482C25.61 -0.000176983 25.4732 0.0266435 25.3456 0.0789654C25.218 0.131287 25.1021 0.208074 25.0046 0.304873C23.7323 1.56808 23.0184 3.2791 23.0195 5.06252V10.8282C23.0195 10.9401 23.0645 11.0473 23.1444 11.1264C23.2244 11.2056 23.3328 11.25 23.4459 11.25H23.8722V16.8751C23.8722 17.1734 23.992 17.4596 24.2052 17.6705C24.4184 17.8815 24.7076 18.0001 25.0091 18.0001C25.3107 18.0001 25.5998 17.8815 25.813 17.6705C26.0262 17.4596 26.146 17.1734 26.146 16.8751V0.337488C26.1461 0.260861 26.1197 0.186543 26.0714 0.126687C26.023 0.0668316 25.9555 0.02503 25.88 0.00817199ZM25.2934 16.8751C25.2934 16.9496 25.2634 17.0212 25.2101 17.0739C25.1568 17.1267 25.0845 17.1563 25.0091 17.1563C24.9337 17.1563 24.8615 17.1267 24.8082 17.0739C24.7549 17.0212 24.7249 16.9496 24.7249 16.8751V11.25H25.2934V16.8751ZM25.2934 10.4063H23.8722V5.06252C23.8701 3.86035 24.2405 2.68643 24.9335 1.69904C25.0453 1.54023 25.1653 1.38635 25.2934 1.23748V10.4063Z"
        fill="#43A047"
      />
      <ellipse cx="11.3673" cy="4.78141" rx="0.852685" ry="0.843911" fill="#43A047" />
    </svg>
  );
};

const HomeScreenWidget: FC<Props> = (props) => {
  const {
    buyer,
    noteItemLoading,
    showcase,
    showcaseId,
    sections,
    categories,
    loading,
    addedProducts,
    addingProducts,
    permissions,
    onToggleFavorite,
    onSetProductNote,
    gotoSignIn,
  } = props;
  const section = sections[showcaseId] as ISectionData;
  const loggedIn = !!localStorage.getItem('token');
  const lang = localStorage.getItem('lang') || 'vi';
  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [isOpenNoteModal, setIsOpenNoteModal] = useState<boolean>(false);

  const seeAllUrl = useMemo(() => {
    let pathname = '/';
    let search = '';
    if ('filter' in showcase) {
      if (showcase.filter.categoryName) {
        pathname += generateProductSlug(showcase.filter.categoryName, `c${showcase.id}`, lang);
      }
      if (showcase.filter.tags) {
        pathname += `${showcase.filter.tags}`;
      }
    } else if (isRecommendationTag(showcaseId)) {
      pathname += `RECOMMENDED_FOR_YOU`;
      if (buyer?.businessCategory) {
        search = `?businessCategory=${buyer.businessCategory}`;
      }
    }
    return { pathname: pathname, search: search };
  }, [showcase, buyer.businessCategory]);

  const trackingFrom = useMemo<ITrackingFrom>(() => {
    if ('filter' in showcase) {
      if (!!showcase.filter.tags) {
        return {
          productImpression: TrackingProductImpressionType.HomeTag,
          addToCart: TrackingAddToCartAddFromType.HomeTag,
        };
      }
      if (!!showcase.filter.categoryName) {
        return {
          productImpression: TrackingProductImpressionType.HomeCategory,
          addToCart: TrackingAddToCartAddFromType.HomeCategory,
        };
      }
    }
    // isRecommendation
    return {
      productImpression: TrackingProductImpressionType.HomeRecommendation,
      addToCart: TrackingAddToCartAddFromType.HomeRecommendation,
    };
  }, [showcase]);

  const isTopGrade = showcaseId === 'AGRADE_TAG';

  const cardSpacing = (width, isTopGrade = false) => {
    if (width < 991) {
      return 8;
    }
    if (width > 991 && !isTopGrade) {
      return 24;
    }
    return 16;
  };

  const onClickOpenNoteModal = useCallback(
    (product: Product, index: number) => {
      if (loggedIn) {
        setSelectedProduct(product);
        setSelectedIndex(index);
        setIsOpenNoteModal(true);
      } else {
        gotoSignIn();
      }
    },
    [setSelectedProduct, setSelectedIndex, setIsOpenNoteModal, loggedIn],
  );

  const onClickCloseNoteModal = useCallback(() => {
    setSelectedProduct(undefined);
    setSelectedIndex(-1);
    setIsOpenNoteModal(false);
  }, [setSelectedProduct, setSelectedIndex, setIsOpenNoteModal]);

  const onSubmitAddingNote = useCallback(
    (note: string) => {
      if (selectedProduct) {
        onSetProductNote({
          index: selectedIndex,
          section: showcaseId,
          note: note,
          id: selectedProduct.id,
        });
      }
      onClickCloseNoteModal();
    },
    [selectedProduct, showcaseId, selectedIndex, onClickCloseNoteModal],
  );
  if ((!loading[showcaseId] && !section?.data?.length) || sections[showcaseId].error) {
    return null;
  }
  return section?.data?.length ? (
    <Viewport.Consumer>
      {({ width }) => (
        <Container id={showcaseId}>
          <WidgetWrapper isTopGrade={isTopGrade}>
            <SectionTitle isTopGrade={isTopGrade}>
              {showcaseId === 'BEST_DEALS' ? (
                <BestDealWrapper>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M3.31738 12.4983C3.31738 11.3501 3.74033 10.2101 4.33789 9.46605C4.56372 9.18497 4.94208 9.07692 5.28223 9.19652C5.62247 9.31623 5.85059 9.63759 5.85059 9.99828C5.85059 10.3254 5.98062 10.6391 6.21191 10.8703C6.44319 11.1015 6.75696 11.2317 7.08398 11.2317C7.41097 11.2316 7.72484 11.1016 7.95605 10.8703C8.18725 10.6391 8.31738 10.3253 8.31738 9.99828C8.31738 9.10311 8.04291 8.8175 7.57324 7.87816C7.02955 6.79105 6.95652 5.70529 7.33691 4.64867C7.70308 3.63158 8.46289 2.714 9.44043 1.85863C9.6687 1.65889 9.98621 1.5961 10.2734 1.69359C10.5606 1.79108 10.7744 2.03394 10.834 2.33128C11.2098 4.21043 12.3474 6.03763 13.8643 7.25121C15.6836 8.70668 16.6836 10.5166 16.6836 12.4983C16.6836 13.3759 16.5107 14.245 16.1748 15.0559C15.839 15.8666 15.347 16.6033 14.7266 17.2239C14.106 17.8445 13.3685 18.3372 12.5576 18.6731C11.7469 19.0089 10.8775 19.1819 10 19.1819C9.12244 19.1818 8.25314 19.0089 7.44238 18.6731C6.63161 18.3372 5.89496 17.8444 5.27441 17.2239C4.65393 16.6033 4.16201 15.8666 3.82617 15.0559C3.4903 14.245 3.31738 13.3759 3.31738 12.4983ZM10.0166 9.99828C10.0166 10.7762 9.70821 11.5224 9.1582 12.0725C8.60817 12.6225 7.86184 12.9318 7.08398 12.9319C6.32154 12.9319 5.59068 12.6334 5.04395 12.1037C5.02768 12.2339 5.0166 12.3658 5.0166 12.4983C5.0166 13.1527 5.14605 13.8009 5.39648 14.4055C5.64689 15.01 6.01395 15.5591 6.47656 16.0217C6.93931 16.4845 7.48914 16.8514 8.09375 17.1018C8.69819 17.3521 9.34577 17.4816 10 17.4817C10.6544 17.4817 11.3026 17.3522 11.9072 17.1018C12.5118 16.8514 13.0617 16.4844 13.5244 16.0217C13.987 15.5591 14.3541 15.0099 14.6045 14.4055C14.8549 13.8009 14.9834 13.1527 14.9834 12.4983C14.9834 11.1466 14.3167 9.78954 12.8027 8.57835C11.3798 7.43997 10.2376 5.85911 9.58887 4.12816C9.2762 4.5136 9.06146 4.8778 8.93652 5.22484C8.72373 5.81612 8.74434 6.4199 9.09375 7.11839C9.45742 7.84568 10.0166 8.59352 10.0166 9.99828Z"
                      fill="#F25B50"
                    />
                  </svg>
                  <BestDealText>
                    {'filter' in showcase && showcase.filter.categoryName
                      ? categories.find((category) => category.name === showcase.filter.categoryName)?.nameLocal ||
                        showcase.title
                      : showcase.title}
                  </BestDealText>
                </BestDealWrapper>
              ) : (
                <SectionHeading id={showcase.title} level={2}>
                  {isTopGrade ? (
                    <TopGradeTitle>
                      {lang === 'vi' ? (
                        <>
                          <span>Hàng tuyển chọn</span> <TopGradeTitleHighlight>Loại 1</TopGradeTitleHighlight>
                        </>
                      ) : (
                        <>
                          <span>
                            Selected Products <TopGradeTitleHighlight>A</TopGradeTitleHighlight>-Grade
                          </span>
                        </>
                      )}
                    </TopGradeTitle>
                  ) : (
                    <>
                      {'filter' in showcase && showcase.filter.categoryName
                        ? categories.find((category) => category.name === showcase.filter.categoryName)?.nameLocal ||
                          showcase.title
                        : showcase.title}
                    </>
                  )}
                </SectionHeading>
              )}
              <Link
                to={{
                  pathname: seeAllUrl.pathname,
                  search: seeAllUrl.search,
                  state: {
                    route: 'HomeScreenWidget',
                    state: {
                      data: section.data,
                    },
                  },
                }}
                style={{ color: isTopGrade ? '#ffffff' : '#136FD8' }}
              >
                <SeeAllWrapper isTopGrade={isTopGrade} isBestDeal={showcaseId === 'BEST_DEALS'}>
                  <FormattedMessage {...utilsMessages[width > 991 ? 'seeAll' : 'seeAllMobile']} />
                  <Arrow color={isTopGrade ? '#ffffff' : showcaseId === 'BEST_DEALS' ? '#E56910' : ''} />
                </SeeAllWrapper>
              </Link>
            </SectionTitle>
            <List gap={showcaseId === 'BEST_PRICES'} isTopGrade={isTopGrade}>
              {showcaseId === 'BEST_PRICES' && width > 1023 && (
                <BannerWrapper>
                  <BannerImage src={bestPriceBg} />
                  <BannerTextbox>
                    <KamereoLogo />
                    {lang === 'vi' ? (
                      <BannerText>
                        Giá Tốt<span>Chất lượng, giá ổn định</span>
                      </BannerText>
                    ) : (
                      <BannerText>
                        Best prices <span>Quality, stable prices</span>
                      </BannerText>
                    )}
                    <BannerTime>{`${start.format('DD/MM')} - ${end.format('DD/MM')}`}</BannerTime>
                  </BannerTextbox>
                </BannerWrapper>
              )}
              <SwiperElement
                slidesPerView={'auto'}
                spaceBetween={cardSpacing(width, isTopGrade)}
                navigation={true}
                modules={[Navigation, Mousewheel]}
                mousewheel={{ forceToAxis: true }}
                simulateTouch={true}
                className="mySwiper"
                slidesPerGroup={2}
                isBestPrices={showcaseId === 'BEST_PRICES'}
              >
                {showcaseId === 'BEST_PRICES' && width < 1024 && (
                  <SwiperSlideElement key="STATIC_BANNER" width={136}>
                    <BannerWrapper>
                      <BannerImage src={bestPriceBgMobile} width={136} />
                      <BannerTextbox>
                        <KamereoLogo />
                        {lang === 'vi' ? (
                          <BannerText>
                            Giá Tốt<span>Chất lượng, giá ổn định</span>
                          </BannerText>
                        ) : (
                          <BannerText>
                            Best prices <span>Quality, stable prices</span>
                          </BannerText>
                        )}
                        <BannerTime isMobile={true}>{`${start.format('DD/MM')} - ${end.format('DD/MM')}`}</BannerTime>
                      </BannerTextbox>
                    </BannerWrapper>
                  </SwiperSlideElement>
                )}
                {(section?.data as Product[]).map((product: Product, index: number) => (
                  <SwiperSlideElement key={product?.id} width={isTopGrade ? (width < 768 ? 160 : 242.4) : undefined}>
                    <Item>
                      <NewMarketItem
                        vertialOnly
                        listName="Section Product List"
                        loggedIn={loggedIn}
                        gotoSignIn={gotoSignIn}
                        product={product}
                        loading={loading[showcaseId]}
                        favorite={product.isFavorite}
                        note={product.note}
                        added={addedProducts[product.id]}
                        adding={addingProducts[product.id]}
                        position={index}
                        permissions={permissions}
                        onToggleFavorite={() =>
                          onToggleFavorite({ product: product, index: index, section: showcaseId })
                        }
                        onOpenNote={() => onClickOpenNoteModal(product, index)}
                        onDeleteNote={() => {
                          onSetProductNote({ index: index, section: showcaseId, note: '', id: product.id });
                          onClickCloseNoteModal();
                        }}
                        impressionProperties={{
                          type: trackingFrom?.productImpression,
                          tag:
                            trackingFrom?.productImpression === TrackingProductImpressionType.HomeTag
                              ? showcaseId
                              : undefined,
                        }}
                        addToCartProperties={{
                          addFrom: trackingFrom?.addToCart,
                          tag:
                            trackingFrom?.addToCart === TrackingAddToCartAddFromType.HomeTag ? showcaseId : undefined,
                        }}
                        hasHorizontalPadding={isTopGrade}
                      />
                    </Item>
                  </SwiperSlideElement>
                ))}
              </SwiperElement>
            </List>
            <ModalNote
              headerLabel={(selectedProduct && selectedProduct.name) || ''}
              isOpen={isOpenNoteModal}
              confirmLoading={noteItemLoading}
              onSubmit={onSubmitAddingNote}
              onClose={onClickCloseNoteModal}
              note={(selectedProduct && selectedProduct.note) || ''}
            />
          </WidgetWrapper>
          {/* {showcaseId === widgets[firstWidgetIndex].widgetId && (
            <MightFavoriteProductsWidget isShowBeside showcaseId={'MIGHT_FAVORITE_PRODUCTS'} />
          )} */}
        </Container>
      )}
    </Viewport.Consumer>
  ) : null;
};

const mapStateToProps = createStructuredSelector<ApplicationRootState, IStateProps>({
  categories: selectCategories(),
  sections: selectSections(),
  loading: selectLoading(),
  addedProducts: selectAddedProducts(),
  addingProducts: selectAddingProducts(),
  permissions: selectUserPermissions(),
  noteItemLoading: selectNoteItemLoading(),
  buyer: selectBuyer(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  onToggleFavorite: (data: IHomeFavoritePayload) => dispatch(setHomeFavirote(data)),
  onSetProductNote: (data: IHomeProductNotePayload) => dispatch(setHomeProductNote(data)),
  gotoSignIn: () => dispatch(setOpenSigninPopup(true)),
});

const withConnect = connect<IStateProps, IDispatchProps, IOwnProps>(mapStateToProps, mapDispatchToProps);

export default withConnect(HomeScreenWidget);
